'use client';

import { useEffect, useState } from 'react';
import { useUiMotionEnabled } from './useMotion';
import { countUpDurationMs } from './countUpDuration';

/**
 * Counts a number from its previous value to the new one so the SIZE of a change is
 * readable, not just its direction. Written once and shared: the spark balance
 * (`BonusCredits animateChange`) and the credits balance (`Credits animateChange`) run
 * the same tween, so the two readouts on the same screen cannot drift apart.
 *
 * The duration scales with the size of the change — see `countUpDuration.ts` for the
 * formula and why it has a floor and a ceiling.
 *
 * Two things it deliberately does NOT do:
 *  - it never animates the first paint: the initial value is the target already;
 *  - it jumps instantly when `enabled` is false. Callers pass `false` while the profile
 *    query has no data, because screens render `balance ?? 0` in that window and a tween
 *    would count the balance down to zero and back up on every load or cache reset.
 *
 * Under `prefers-reduced-motion` the value also jumps: the number still updates, it just
 * does not travel.
 */

// Exact JS replay of CSS cubic-bezier(0.2, 0.8, 0.2, 1) — fast start, gentle settle.
// Same Newton's-method solver as UpgradeDial's tick sync; no shared util for it in this codebase.
function makeCubicBezier(x1: number, y1: number, x2: number, y2: number): (x: number) => number {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sampleX = (t: number): number => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number): number => ((ay * t + by) * t + cy) * t;
  return (x: number): number => {
    let t = Math.max(0, Math.min(1, x));
    for (let i = 0; i < 8; i++) {
      const dx = (3 * ax * t + 2 * bx) * t + cx;
      if (Math.abs(dx) < 1e-6) break;
      t -= (sampleX(t) - x) / dx;
    }
    return sampleY(Math.max(0, Math.min(1, t)));
  };
}
const easeCount = makeCubicBezier(0.2, 0.8, 0.2, 1);

type TweenState = {
  /** Last amount this hook has resolved to — the animation's target once idle. */
  target: number;
  /** Number currently on screen. */
  displayed: number;
};

/**
 * @param amount  the value to reach
 * @param enabled false makes every change land instantly (untracked balance, see above)
 * @returns the number to render right now
 */
export function useCountUp(amount: number, enabled = true): number {
  const motionEnabled = useUiMotionEnabled();
  const animates = enabled && motionEnabled;
  const [tween, setTween] = useState<TweenState>({ target: amount, displayed: amount });

  // Target changes are resolved during render, the same trick `CreditsChangeFlash` uses:
  // decide the new `displayed` before this paint, so there is never a one-frame stale
  // number. Only the per-frame tween needs an effect — real, unavoidable async work.
  if (tween.target !== amount) {
    setTween({ target: amount, displayed: animates ? tween.displayed : amount });
  }

  useEffect(() => {
    if (tween.displayed === tween.target) return;
    const start = tween.displayed;
    const target = tween.target;
    const delta = target - start;
    const durationMs = countUpDurationMs(delta);
    const t0 = performance.now();
    let raf = 0;
    const step = (now: number): void => {
      const p = Math.min(1, (now - t0) / durationMs);
      const value = Math.round(start + delta * easeCount(p));
      setTween((s) => (s.target === target ? { target, displayed: value } : s));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // Only a genuine target change should (re)start the tween — `tween.displayed`
    // changing on every one of its own steps must not restart the loop it's driving.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tween.target]);

  return tween.displayed;
}
